/* Gunrange shop — the cart: the drawer (sections/gr-cart-drawer.liquid, layer
   "gr-cart") and the cart page (sections/gr-main-cart.liquid). Both render
   the same snippets; this one module keeps them current.

   A cart view is any [data-gr-cart-section] (data-gr-change-url is
   routes.cart_change_url). After every change the server re-renders it
   (Section Rendering, in the same request) and only its [data-gr-cart-body]
   is swapped: the drawer layer and the view's live region
   ([data-gr-cart-status]) stay, focus returns to the same control, the
   drawer keeps its scroll, the free-delivery bar fills from its old width
   (450ms) and the express checkout buttons ([data-gr-keep]) aren't
   restarted.

   Changes (POST /cart/change.js { id: line key, quantity }):
   - The stepper or a typed quantity is optimistic: the number changes at
     once, the line is marked busy, the request goes after a 300ms pause
     (rapid clicks send one request). Requests run one at a time; while more
     are queued the page isn't re-rendered in between.
   - Remove (a link to line.url_to_remove, so it also works without
     JavaScript) hides the line at once and moves focus to the next line.
   - A refusal is never taken to mean "nothing changed": Shopify answers a
     quantity above stock with a 422 AFTER setting the line to what it could
     ("Only 50 items were added…"). So after any refusal the cart is read
     back from the store (GR.cart.refresh) and the view shows that; the line
     then says why: Shopify's own message for a 422, the view's generic one
     otherwise. A line that went back to its old quantity did so because the
     store says so, not because we assumed it. When Shopify sets fewer than
     asked (a 200 or a 422), the line says so.
   - Checkout waits for queued changes and for a read-back in progress to
     finish, then submits the re-rendered form (its quantities are
     updates[<line key>], so even an old form can't move one line's
     quantity onto another).
   - Out of date: while the tab was hidden the cart may have changed
     elsewhere (another tab). The cart page and an open drawer are read back
     when the tab is visible again, a closed drawer when it next opens, a
     page restored from the back/forward cache at once. A read-back that
     fails reloads the page rather than leave an old cart form on it.
   Copy is never written here: every message comes from the view's
   data-gr-msg-* attributes (snippets/gr-cart-body-attrs.liquid).

   Add to cart (gr-product.js posts /cart/add.js with GR.cart.sectionIds()):
   on `gr:cart-add` this renders the returned sections and opens the drawer
   (on the cart page the page shows them). When they can't be shown: the
   cart page reloads, the drawer isn't opened and the cart page is loaded.
   A refused add calls refresh() for the same reason as above.

   GR.cart:
     sectionIds()      ids of the sections to re-render with a cart change:
                       every section holding a header cart button
                       ([data-gr-cart-button]) or a cart view
     render(sections)  apply { id: html } from a cart response; false when a
                       cart view on the page wasn't in it
     refresh()         re-render every cart section (GET ?sections=) */
(() => {
  'use strict';

  const GR = window.GR;
  if (!GR || !GR.layer) {
    console.error('[gr-cart] gr-shop.js has not loaded; the cart works as plain pages.');
    return;
  }
  if (GR.cart) return; // the drawer and the cart page each include this file; one copy runs

  const DEBOUNCE_MS = 300;
  const CHECKOUT_WAIT_MS = 5000; // the longest checkout waits for pending changes before posting the form as it is
  const PREFIX = 'shopify-section-';
  const DRAWER = 'gr-cart';

  // The page is going: to a reload (the page itself is the honest answer
  // when it can't show the store's cart) or to checkout. Whichever comes
  // first wins; nothing is submitted or reloaded after it.
  let leaving = false;
  const reload = () => {
    if (leaving) return;
    leaving = true;
    window.location.reload();
  };

  /* ── Sections ────────────────────────────────────────────────────────── */
  const sectionIdOf = (el) => {
    const section = el.closest('.shopify-section');
    return section && section.id.startsWith(PREFIX) ? section.id.slice(PREFIX.length) : null;
  };

  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('[data-gr-cart-button], [data-gr-cart-section]').forEach((el) => {
      const id = sectionIdOf(el);
      if (id) ids.add(id);
    });
    return Array.from(ids);
  };

  const views = () => Array.from(document.querySelectorAll('[data-gr-cart-section]'));
  const bodyOf = (view) => view.querySelector('[data-gr-cart-body]');
  const lineIn = (view, key) => view.querySelector(`[data-gr-line="${CSS.escape(key)}"]`);

  /* ── Focus across a swap ─────────────────────────────────────────────── */
  const ROLES = ['[data-gr-qty="down"]', '[data-gr-qty="up"]', '.qty input', '[data-gr-line-remove]'];

  // Where focus is inside a view, as something that survives a re-render.
  const focusRef = (view) => {
    const el = document.activeElement;
    if (!el || el === document.body || !view.contains(el)) return null;
    const line = el.closest('[data-gr-line]');
    const role = line ? ROLES.find((selector) => el.matches(selector)) : null;
    if (line && role) return { line: line.dataset.grLine, role };
    return { id: el.id || null };
  };

  const restoreFocus = (view, ref) => {
    if (!ref) return;
    let target = null;
    if (ref.line) {
      const line = lineIn(view, ref.line);
      target = line && line.querySelector(ref.role);
    } else if (ref.id) {
      target = document.getElementById(ref.id);
    }
    if (!target || !view.contains(target)) target = view.querySelector('[data-gr-cart-focus]');
    if (target) target.focus({ preventScroll: true });
  };

  /* ── Rendering ───────────────────────────────────────────────────────── */
  // The free-delivery bar starts at its old width and fills to the new one
  // (450ms, gr-shop.css). The start is set before the new body is in the
  // page: anything that forces a layout first (restoring the scroll, focus)
  // would otherwise lay the bar out at its end width and skip the fill.
  const holdFill = (next, width) => {
    const fill = next.querySelector('[data-gr-ship-fill]');
    if (!fill) return () => {};
    const target = fill.style.getPropertyValue('--gr-fill');
    fill.style.setProperty('--gr-fill', width || '0px');
    return () => {
      fill.getBoundingClientRect(); // lay out the start width
      fill.style.setProperty('--gr-fill', target);
    };
  };

  const swap = (view, fresh, focus) => {
    const body = bodyOf(view);
    if (!body) return;
    const ref = focus === undefined ? focusRef(view) : focus;
    const scroller = body.querySelector('[data-gr-cart-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const oldFill = body.querySelector('[data-gr-ship-fill]');
    const width = oldFill ? getComputedStyle(oldFill).width : null;

    const next = document.importNode(fresh, true);
    next.querySelectorAll('[data-gr-keep]').forEach((slot) => {
      const kept = body.querySelector(`[data-gr-keep="${CSS.escape(slot.dataset.grKeep)}"]`);
      if (kept) slot.replaceWith(kept);
    });
    const fill = holdFill(next, width);
    body.replaceWith(next);
    fill();

    const nextScroller = next.querySelector('[data-gr-cart-scroll]');
    if (nextScroller) nextScroller.scrollTop = scrollTop;
    restoreFocus(view, ref);
  };

  // sections: { id: html | null } from /cart/add.js, /cart/change.js or ?sections=.
  const render = (sections, focus) => {
    if (!sections || typeof sections !== 'object') return false;
    const docs = {};
    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html === 'string') docs[id] = new DOMParser().parseFromString(html, 'text/html');
    });

    const button = Object.values(docs)
      .map((doc) => doc.querySelector('[data-gr-cart-button]'))
      .find(Boolean);
    if (button) {
      document.querySelectorAll('[data-gr-cart-button]').forEach((old) => old.replaceWith(document.importNode(button, true)));
    }

    let complete = true;
    views().forEach((view) => {
      const id = sectionIdOf(view);
      const fresh = id && docs[id] ? docs[id].querySelector('[data-gr-cart-body]') : null;
      if (fresh) swap(view, fresh, focus);
      else complete = false;
    });
    GR.layer.wire();
    return complete;
  };

  // skip (optional) is asked once the cart has arrived: true leaves the page
  // as it is and resolves null (a change made during the request will
  // render the cart when it's answered; rendering now would overwrite it).
  const refresh = async (skip) => {
    const ids = sectionIds();
    if (!ids.length) return true;
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const sections = await response.json();
      if (skip && skip()) return null;
      return render(sections);
    } catch (error) {
      console.error('[gr-cart] The cart could not be re-rendered.', error);
      return false;
    }
  };

  // Say it once in the view's live region (cleared first so a repeat is announced).
  const announce = (view, text) => {
    const status = view.querySelector('[data-gr-cart-status]');
    if (!status || !text) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  };

  const message = (view, name, fill = {}) => {
    const body = bodyOf(view);
    let text = body ? body.getAttribute(`data-gr-msg-${name}`) || '' : '';
    Object.entries(fill).forEach(([key, value]) => {
      text = text.replaceAll(`{${key}}`, value);
    });
    return text;
  };

  /* ── Lines ───────────────────────────────────────────────────────────── */
  const setBusy = (line, busy) => {
    if (busy) line.setAttribute('aria-busy', 'true');
    else line.removeAttribute('aria-busy');
  };

  const showLineError = (line, text) => {
    const slot = line.querySelector('[data-gr-line-error]');
    if (slot) slot.textContent = text || '';
  };

  // Back to the last quantity the server rendered, and the line if it was
  // hidden for removal. Only for input that was never sent (an empty or
  // negative number): anything the store answered is read back instead.
  const rollback = (line) => {
    line.hidden = false;
    setBusy(line, false);
    const input = line.querySelector('.qty input');
    if (input && input.value !== line.dataset.grQtyCommitted) {
      input.value = line.dataset.grQtyCommitted;
      input.dispatchEvent(new Event('change', { bubbles: true })); // re-syncs the stepper; no request (value = committed)
    }
  };

  /* ── The queue: one request at a time, the latest quantity per line ───── */
  const queue = new Map(); // line key → { view, quantity, title, focus }
  let inFlight = null; // the key being sent
  let sending = null; // its request
  let draining = null; // Promise while the queue runs
  let unrendered = null; // the newest successful answer, not rendered yet because more changes were queued
  let reread = false; // a refused change may still have changed the cart: read it back before showing it
  const notes = new Map(); // line key → { view, requested, before, shopify, failed }, shown once the view is current
  const queued = () => queue.size > 0;

  const post = async (view, key, quantity) => {
    const response = await fetch(`${view.dataset.grChangeUrl}.js`, {
      method: 'POST',
      keepalive: true, // lands even if the page is left while it's under way
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname })
    });
    const data = await response.json().catch(() => null);
    return { ok: response.ok && !!data && Array.isArray(data.items), status: response.status, data };
  };

  // Shopify's own reason for a 422 ("Only 50 items were added…", "You can only add 2…").
  const shopifyReason = (result) => {
    if (result.status !== 422) return '';
    const data = result.data || {};
    return data.description || data.message || (typeof data.errors === 'string' ? data.errors : '');
  };

  // Once the view shows the store's cart: each changed line says what
  // happened, measured against what the store now holds for it.
  const showNotes = () => {
    const unplaced = [];
    notes.forEach((note, key) => {
      const line = lineIn(note.view, key);
      const held = line ? Number(line.dataset.grQtyCommitted) : 0;
      // Capped: the store holds fewer than asked. After a refusal only if the
      // line moved at all — back at its old quantity means nothing changed.
      const capped = note.requested > 0 && held > 0 && held < note.requested && (!note.failed || held !== note.before);
      let text = note.shopify;
      if (!text && capped) text = message(note.view, 'limited', { n: held });
      if (!text && note.failed) text = message(note.view, 'error');
      if (line) {
        showLineError(line, text);
        if (note.failed && note.requested === 0) restoreFocus(note.view, { line: key, role: '[data-gr-line-remove]' });
      } else if (text) {
        unplaced.push(text);
      }
    });
    notes.clear();
    return unplaced;
  };

  const send = async (key, job) => {
    const { view, quantity } = job;
    const sent = lineIn(view, key);
    const before = sent ? Number(sent.dataset.grQtyCommitted) : 0;
    inFlight = key;
    sending = post(view, key, quantity);
    let result;
    try {
      result = await sending;
    } catch (error) {
      console.error('[gr-cart] The cart change did not reach the store.', error);
      result = { ok: false, status: 0, data: null };
    } finally {
      inFlight = null;
      sending = null;
    }
    if (result.ok) {
      // The answer is the whole cart after every change so far.
      unrendered = result.data;
      reread = false;
      notes.set(key, { view, requested: quantity, before, shopify: '', failed: false });
    } else {
      console.error(`[gr-cart] The cart change was refused (HTTP ${result.status}).`, result.data);
      // Whatever this page last knew may be out of date now (a 422 above
      // stock has already set the capped quantity): read the cart back.
      unrendered = null;
      reread = true;
      notes.set(key, { view, requested: quantity, before, shopify: shopifyReason(result), failed: true });
    }
    if (queue.size) return; // show the cart once, after the last queued change

    let current = true;
    if (unrendered) current = render(unrendered.sections, job.focus) || (await refresh(queued));
    else if (reread) current = await refresh(queued);
    if (current === null) return; // changed during the read-back: that answer shows the cart and the notes
    unrendered = null;
    reread = false;
    if (!current) {
      reload(); // the cart may have changed and this page can't show it
      return;
    }
    const unplaced = showNotes();
    const body = bodyOf(view);
    const removed = quantity === 0 && result.ok ? message(view, 'removed', { title: job.title }) : '';
    announce(view, [removed, ...unplaced, body ? body.dataset.grCartAnnounce : ''].filter(Boolean).join(' '));
  };

  const drain = () => {
    if (draining) return draining;
    draining = (async () => {
      while (queue.size) {
        const [key, job] = queue.entries().next().value;
        queue.delete(key);
        await send(key, job);
      }
    })().finally(() => {
      draining = null;
    });
    return draining;
  };

  const drainSoon = GR.debounce(drain, DEBOUNCE_MS);

  // now: send without the pause (a removal).
  const schedule = (view, line, quantity, now = false) => {
    const key = line.dataset.grLine;
    const job = { view, quantity, title: line.dataset.grLineTitle || '', focus: undefined };
    queue.set(key, job);
    setBusy(line, true);
    showLineError(line, '');
    if (now) {
      drainSoon.cancel();
      drain();
    } else {
      drainSoon();
    }
    return job;
  };

  // Leaving within the debounce (a link, closing the tab, the express
  // buttons' own checkout) would drop the queued changes. They go now, all at
  // once (keepalive, one per line; a line already being sent goes after its
  // request), and nothing is rendered from their answers: if the page stays,
  // the cart is read back once they, and any change under way, are answered.
  const flush = () => {
    drainSoon.cancel();
    if (!queue.size) return;
    const jobs = Array.from(queue);
    queue.clear();
    const busy = { key: inFlight, request: sending };
    const sent = jobs.map(([key, job]) => {
      const go = () => post(job.view, key, job.quantity);
      const request = key === busy.key && busy.request ? busy.request.then(go, go) : go();
      return request.catch((error) => console.error('[gr-cart] A cart change sent on leaving did not reach the store.', error));
    });
    Promise.all(sent)
      .then(() => draining)
      .then(() => {
        if (!leaving) readBack();
      });
  };

  const remove = (view, line) => {
    const lines = Array.from(view.querySelectorAll('[data-gr-line]:not([hidden])'));
    const at = lines.indexOf(line);
    const neighbour = lines[at + 1] || lines[at - 1] || null;
    const job = schedule(view, line, 0, true);
    const input = line.querySelector('.qty input');
    if (input) input.value = '0'; // what the cart form posts for it if checkout can't wait (checkoutAfter)
    // Focus moves on now, before the line goes; the re-render keeps it there.
    job.focus = neighbour ? { line: neighbour.dataset.grLine, role: '[data-gr-line-remove]' } : { id: null };
    line.hidden = true;
    restoreFocus(view, job.focus);
  };

  /* ── Out of date: read the cart back ─────────────────────────────────── */
  // The cart may have changed elsewhere (another tab) while this page was
  // hidden or in the back/forward cache. Checkout waits for a read-back in
  // progress; one that fails reloads the page.
  let stale = false;
  let readingBack = null; // Promise while a read-back runs

  const readBack = () => {
    if (queue.size || draining) return Promise.resolve(); // the queued changes' answer is the whole cart
    if (!readingBack) {
      stale = false;
      readingBack = refresh(queued)
        .then((current) => {
          if (current === false) reload(); // null: a change came in, and its answer shows the cart
        })
        .finally(() => {
          readingBack = null;
        });
    }
    return readingBack;
  };

  // The cart page's view, or the drawer while it's open.
  const onScreen = () => views().some((view) => view.id !== DRAWER || view.hasAttribute('data-open'));

  /* ── Events ──────────────────────────────────────────────────────────── */

  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.matches || !input.matches('[data-gr-line] .qty input')) return;
    const view = input.closest('[data-gr-cart-section]');
    const line = input.closest('[data-gr-line]');
    if (!view || !line || !view.dataset.grChangeUrl) return;
    const key = line.dataset.grLine;
    const value = Number(input.value);
    if (input.value.trim() === '' || !Number.isInteger(value) || value < 0) {
      rollback(line);
      return;
    }
    if (value === 0) {
      remove(view, line);
      return;
    }
    const settled = value === Number(line.dataset.grQtyCommitted) && !queue.has(key) && inFlight !== key;
    if (settled) {
      setBusy(line, false);
      return;
    }
    schedule(view, line, value);
  });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-gr-line-remove]');
    if (!link) return;
    const view = link.closest('[data-gr-cart-section]');
    const line = link.closest('[data-gr-line]');
    if (!view || !line || !view.dataset.grChangeUrl) return; // stays a plain link
    e.preventDefault();
    remove(view, line);
  });

  // Checkout once `wait` (the read-back, then the queued changes) is done:
  // the re-render replaced the form, so its successor is submitted, unless
  // checkout is now blocked (a line sold out). If the wait fails or takes
  // longer than CHECKOUT_WAIT_MS, the form goes as it stands, not
  // intercepted: its updates[<line key>] carry the quantities on screen (a
  // line being removed holds 0), and Shopify applies them before checkout.
  const checkoutAfter = (form, wait) => {
    const formId = form.id;
    let done = false;
    const postAsItStands = (reason, error) => {
      if (done || leaving) return;
      done = true;
      leaving = true;
      console.error(`[gr-cart] ${reason}; posting the cart form to checkout as it stands.`, error || '');
      const target = document.getElementById(formId) || form;
      const flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'checkout';
      target.append(flag);
      target.submit();
    };
    const timeout = setTimeout(() => postAsItStands(`Cart changes were still pending after ${CHECKOUT_WAIT_MS}ms`), CHECKOUT_WAIT_MS);
    wait
      .then(() => {
        clearTimeout(timeout);
        if (done || leaving) return;
        done = true;
        const next = document.getElementById(formId);
        const checkout = next && document.querySelector(`[name="checkout"][form="${CSS.escape(formId)}"]`);
        if (next && checkout && !checkout.disabled) next.requestSubmit(checkout);
      })
      .catch((error) => {
        clearTimeout(timeout);
        postAsItStands('Waiting for the cart changes failed', error);
      });
  };

  // The cart form: checkout goes once queued changes are in; Enter in a
  // quantity field is a change (handled above), not a page post.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('[data-gr-cart-form]');
    if (!form) return;
    const submitter = e.submitter;
    if (!submitter || submitter.name !== 'checkout') {
      e.preventDefault();
      const active = document.activeElement;
      if (active && active.matches('.qty input') && form.contains(active)) {
        active.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    if (!queue.size && !draining && !inFlight && !readingBack) return;
    e.preventDefault();
    drainSoon.cancel();
    checkoutAfter(form, Promise.resolve(readingBack).then(drain));
  });

  // An add anywhere on the page: show the new cart and open the drawer.
  document.addEventListener('gr:cart-add', async (e) => {
    const detail = e.detail || {};
    let current = render(detail.sections, null);
    if (!current) current = await refresh();
    const drawer = document.getElementById(DRAWER);
    if (!current) {
      if (drawer) window.location.assign(drawer.dataset.grCartUrl || '/cart'); // don't open a drawer that's out of date
      else reload(); // the cart page: it can't show the new cart
      return;
    }
    if (!drawer) return; // the cart page: the page itself shows the new cart
    stale = false;
    GR.layer.open(DRAWER);
  });

  // The express buttons check out on their own (a pointer or a key press on
  // them): queued changes go first.
  const beforeExpress = (e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target.closest && e.target.closest('[data-gr-keep="express"]')) flush();
  };
  document.addEventListener('pointerdown', beforeExpress, true);
  document.addEventListener('keydown', beforeExpress, true);
  window.addEventListener('pagehide', flush);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') stale = true;
    else if (stale && onScreen()) readBack();
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) readBack();
  });
  document.addEventListener('gr:layer-open', (e) => {
    if (e.target.id === DRAWER && stale) readBack();
  });

  GR.cart = { sectionIds, render: (sections) => render(sections), refresh };
})();
